<script setup>
  const props = defineProps({
    error: {
      type: Object,
      default: () => ({ statusCode: 500, statusMessage: 'Internal Server Error' })
    }
  })

  const localePath = useLocalePath()
</script>

<template>
  <NuxtLayout name="blank">
    <section class="relative overflow-hidden min-h-screen flex items-center bg-black">
      <!-- Animated Background with Parallax Layers -->
      <div class="absolute inset-0 overflow-hidden">
        <!-- Gradient Background -->
        <div class="absolute inset-0 bg-gradient-to-br from-sky-900 via-slate-900 to-black opacity-90"></div>

        <!-- Particle Network Effect -->
        <div class="absolute inset-0 opacity-20">
          <div class="absolute top-1/4 left-1/4 w-2 h-2 bg-sky-400 rounded-full animate-float-particle"></div>
          <div
            class="absolute top-1/3 right-1/3 w-2 h-2 bg-teal-400 rounded-full animate-float-particle animation-delay-1000"
          ></div>
          <div
            class="absolute bottom-1/4 left-1/3 w-2 h-2 bg-sky-300 rounded-full animate-float-particle animation-delay-2000"
          ></div>
          <div
            class="absolute top-2/3 right-1/4 w-2 h-2 bg-teal-300 rounded-full animate-float-particle animation-delay-3000"
          ></div>

          <!-- Connecting Lines -->
          <svg class="absolute inset-0 w-full h-full">
            <line
              x1="25%"
              y1="25%"
              x2="66%"
              y2="33%"
              stroke="rgba(56, 189, 248, 0.2)"
              stroke-width="1"
              class="animate-draw-line"
            />
            <line
              x1="66%"
              y1="33%"
              x2="33%"
              y2="75%"
              stroke="rgba(99, 102, 241, 0.2)"
              stroke-width="1"
              class="animate-draw-line animation-delay-500"
            />
          </svg>
        </div>

        <!-- Animated background blobs -->
        <div class="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
          <div
            class="absolute top-1/4 right-1/4 w-96 h-96 bg-sky-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"
          ></div>
          <div
            class="absolute bottom-1/4 left-1/4 w-96 h-96 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"
          ></div>
        </div>
      </div>

      <!-- Content Container -->
      <div class="relative w-full">
        <div class="w-full mx-auto container px-4 sm:px-6 lg:px-8">
          <div class="py-12 sm:py-16 md:py-20">
            <div class="max-w-4xl mx-auto">
              <!-- Error Content Card -->
              <div
                class="relative bg-white/5 backdrop-blur-xl rounded-3xl border-2 border-sky-400/30 shadow-2xl overflow-hidden animate-fade-in-up"
              >
                <!-- Decorative gradient bar -->
                <div class="h-2 bg-gradient-to-r from-sky-500 via-teal-500 to-sky-600"></div>

                <div class="px-6 py-10 sm:px-12 sm:py-16 text-center">
                  <!-- Error Icon -->
                  <div class="mb-6 animate-slide-in-left">
                    <div
                      class="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-sky-500/20 to-teal-500/20 border-2 border-sky-400/50 backdrop-blur-xl mb-6"
                    >
                      <font-awesome
                        :icon="['fas', props.error?.statusCode === 404 ? 'compass' : 'exclamation-triangle']"
                        class="text-4xl text-sky-400"
                      />
                    </div>
                  </div>

                  <!-- Status Code -->
                  <div class="mb-6 animate-slide-in-left animation-delay-200">
                    <h1
                      class="text-7xl sm:text-8xl font-black bg-clip-text text-transparent bg-gradient-to-br from-sky-400 via-sky-300 to-teal-400 mb-4"
                    >
                      {{ props.error?.statusCode || '500' }}
                    </h1>
                    <div class="flex gap-2 items-center justify-center">
                      <div class="h-1 w-24 bg-gradient-to-r from-sky-500 to-teal-500 animate-expand-width"></div>
                      <div
                        class="h-1 w-12 bg-gradient-to-r from-teal-500 to-sky-500 animate-expand-width animation-delay-300"
                      ></div>
                    </div>
                  </div>

                  <!-- Error Message -->
                  <div class="mb-8 animate-slide-in-left animation-delay-400">
                    <h2 class="text-2xl sm:text-3xl font-bold text-white leading-tight mb-3">
                      {{
                        props.error?.statusCode === 404
                          ? $t('pages.errorPage.pageNotFound')
                          : props.error?.statusCode === 500
                            ? $t('pages.errorPage.serverError')
                            : $t('pages.errorPage.generalError')
                      }}
                    </h2>
                    <p class="text-base sm:text-lg text-sky-100 leading-relaxed max-w-2xl mx-auto">
                      {{
                        props.error?.statusCode === 404
                          ? $t('pages.errorPage.pageNotFoundMessage')
                          : props.error?.statusMessage || $t('pages.errorPage.defaultMessage')
                      }}
                    </p>
                  </div>

                  <!-- CTA Buttons -->
                  <div
                    class="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 animate-fade-in-up animation-delay-600"
                  >
                    <NuxtLink :to="localePath('/')" class="hero-btn hero-btn-primary">
                      <font-awesome :icon="['fas', 'home']" class="hero-btn-icon" />
                      <span>{{ $t('pages.errorPage.backToHome') }}</span>
                      <font-awesome :icon="['fas', 'arrow-right']" class="hero-btn-arrow" />
                    </NuxtLink>

                    <button type="button" class="hero-btn hero-btn-secondary" @click="$router.go(-1)">
                      <font-awesome :icon="['fas', 'arrow-left']" class="hero-btn-icon" />
                      <span>{{ $t('pages.errorPage.previousPage') }}</span>
                    </button>
                  </div>

                  <!-- Error Details (Development) -->
                  <div
                    v-if="props.error"
                    class="mt-8 text-left max-w-2xl mx-auto animate-fade-in-up animation-delay-800"
                  >
                    <details
                      class="bg-slate-900/50 backdrop-blur-sm border border-sky-400/30 rounded-xl p-4 hover:border-sky-400/50 transition-colors"
                    >
                      <summary class="cursor-pointer text-sky-300 font-semibold flex items-center gap-2">
                        <font-awesome :icon="['fas', 'info-circle']" class="text-sm" />
                        {{ $t('pages.errorPage.errorDetails') }}
                      </summary>
                      <div class="text-slate-300 text-sm mt-4 space-y-2">
                        <p class="flex items-start gap-2">
                          <strong class="text-sky-400 min-w-20"
                            >{{ $t('pages.errorPage.code') }}{{ $t('global.punctuation.colon') }}</strong
                          >
                          <span>{{ props.error?.statusCode }}</span>
                        </p>
                        <p class="flex items-start gap-2">
                          <strong class="text-sky-400 min-w-20"
                            >{{ $t('pages.errorPage.message') }}{{ $t('global.punctuation.colon') }}</strong
                          >
                          <span>{{ props.error?.statusMessage }}</span>
                        </p>
                        <p v-if="props.error?.data?.error" class="flex items-start gap-2">
                          <strong class="text-sky-400 min-w-20"
                            >{{ $t('pages.errorPage.error') }}{{ $t('global.punctuation.colon') }}</strong
                          >
                          <span>{{ props.error.data.error }}</span>
                        </p>
                        <p v-if="props.error?.data?.context" class="flex items-start gap-2">
                          <strong class="text-sky-400 min-w-20"
                            >{{ $t('pages.errorPage.context') }}{{ $t('global.punctuation.colon') }}</strong
                          >
                          <span>{{ props.error.data.context }}</span>
                        </p>
                      </div>
                    </details>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </NuxtLayout>
</template>

<style scoped>
  @keyframes float-particle {
    0%,
    100% {
      transform: translate(0, 0);
      opacity: 0.3;
    }
    25% {
      transform: translate(10px, -20px);
      opacity: 0.6;
    }
    50% {
      transform: translate(-5px, -40px);
      opacity: 1;
    }
    75% {
      transform: translate(-15px, -20px);
      opacity: 0.6;
    }
  }

  @keyframes draw-line {
    0% {
      stroke-dasharray: 0, 1000;
    }
    100% {
      stroke-dasharray: 1000, 0;
    }
  }

  @keyframes slide-in-left {
    0% {
      opacity: 0;
      transform: translateX(-50px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes expand-width {
    0% {
      width: 0;
    }
    100% {
      width: auto;
    }
  }

  @keyframes fade-in-up {
    0% {
      opacity: 0;
      transform: translateY(30px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes blob {
    0%,
    100% {
      transform: translate(0px, 0px) scale(1);
    }
    33% {
      transform: translate(30px, -50px) scale(1.1);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9);
    }
  }

  .animate-float-particle {
    animation: float-particle 6s ease-in-out infinite;
  }

  .animate-draw-line {
    animation: draw-line 2s ease-in-out forwards;
  }

  .animate-slide-in-left {
    animation: slide-in-left 0.8s ease-out forwards;
    opacity: 0;
  }

  .animate-expand-width {
    animation: expand-width 1s ease-out forwards;
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.8s ease-out forwards;
    opacity: 0;
  }

  .animate-blob {
    animation: blob 7s infinite;
  }

  .animation-delay-200 {
    animation-delay: 0.2s;
  }

  .animation-delay-300 {
    animation-delay: 0.3s;
  }

  .animation-delay-400 {
    animation-delay: 0.4s;
  }

  .animation-delay-500 {
    animation-delay: 0.5s;
  }

  .animation-delay-600 {
    animation-delay: 0.6s;
  }

  .animation-delay-800 {
    animation-delay: 0.8s;
  }

  .animation-delay-1000 {
    animation-delay: 1s;
  }

  .animation-delay-2000 {
    animation-delay: 2s;
  }

  .animation-delay-3000 {
    animation-delay: 3s;
  }
</style>
